import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createMember,
  listLoginEvents,
  listMembers,
  patchMember,
  removeMember,
  resetMemberPassword,
  setMemberDisabled,
  type CreateMemberBody,
  type PatchMemberBody,
} from '../api/team';

export function useTeamMembers() {
  return useQuery({ queryKey: ['team', 'members'], queryFn: listMembers, refetchOnWindowFocus: false });
}

function useInvalidateTeam() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['team'] });
}

export function useCreateMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({ mutationFn: (body: CreateMemberBody) => createMember(body), onSuccess: invalidate });
}

export function usePatchMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: PatchMemberBody }) => patchMember(userId, body),
    onSuccess: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({ mutationFn: (userId: string) => removeMember(userId), onSuccess: invalidate });
}

export function useResetMemberPassword() {
  const invalidate = useInvalidateTeam();
  return useMutation({ mutationFn: (userId: string) => resetMemberPassword(userId), onSuccess: invalidate });
}

export function useSetMemberDisabled() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) => setMemberDisabled(userId, disabled),
    onSuccess: invalidate,
  });
}

export function useLoginEvents(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['team', 'login-events', userId ?? 'all'],
    queryFn: () => listLoginEvents(userId),
    enabled,
    refetchOnWindowFocus: false,
  });
}
